/**
 * Singleton browser socket with reconnect. Subscriptions are re-issued after a reconnect, and
 * each subscription's snapshot + live events land in a ring buffer keyed by subscription id.
 */
import { useEffect, useMemo, useState } from "react";
import { create } from "zustand";
import type { DevicePresence, EventFilter, ServerToBrowserFrame, StoredEvent } from "@facade/dashboard-shared";
import { redirectToLogin } from "./api";

export type WsStatus = "connecting" | "open" | "closed";

interface RunStatusInfo {
  runId: string;
  deviceId: string;
  state: "running" | "ended";
  prompt?: string;
  mode?: string;
  ts: number;
}

interface LiveState {
  status: WsStatus;
  devices: Record<string, DevicePresence>;
  runStatus: Record<string, RunStatusInfo>; // by deviceId: latest
  buffers: Record<string, StoredEvent[]>; // by subscription id
  setStatus: (s: WsStatus) => void;
  setDevices: (d: DevicePresence[]) => void;
  deviceStatus: (f: Extract<ServerToBrowserFrame, { type: "device.status" }>) => void;
  setRunStatus: (f: Extract<ServerToBrowserFrame, { type: "run.status" }>) => void;
  snapshot: (id: string, events: StoredEvent[]) => void;
  push: (id: string, e: StoredEvent) => void;
  drop: (id: string) => void;
}

const CAP = 5000;

export const useLiveStore = create<LiveState>((set) => ({
  status: "connecting",
  devices: {},
  runStatus: {},
  buffers: {},
  setStatus: (status) => set({ status }),
  setDevices: (list) =>
    set((s) => {
      const devices = { ...s.devices };
      for (const d of list) devices[d.deviceId] = { ...devices[d.deviceId], ...d };
      return { devices };
    }),
  deviceStatus: (f) =>
    set((s) => ({
      devices: {
        ...s.devices,
        [f.deviceId]: { ...(s.devices[f.deviceId] ?? { deviceId: f.deviceId }), online: f.online, nickname: f.nickname, lastSeen: f.lastSeen },
      },
    })),
  setRunStatus: (f) =>
    set((s) => ({
      runStatus: { ...s.runStatus, [f.deviceId]: { runId: f.runId, deviceId: f.deviceId, state: f.state, prompt: f.prompt, mode: f.mode, ts: Date.now() } },
      devices: s.devices[f.deviceId]
        ? { ...s.devices, [f.deviceId]: { ...s.devices[f.deviceId], currentRunId: f.state === "running" ? f.runId : null } }
        : s.devices,
    })),
  snapshot: (id, events) => set((s) => ({ buffers: { ...s.buffers, [id]: events.slice(-CAP) } })),
  push: (id, e) =>
    set((s) => {
      const cur = s.buffers[id];
      if (!cur) return s;
      const next = cur.length >= CAP ? cur.slice(cur.length - CAP + 1) : cur.slice();
      next.push(e);
      return { buffers: { ...s.buffers, [id]: next } };
    }),
  drop: (id) =>
    set((s) => {
      const buffers = { ...s.buffers };
      delete buffers[id];
      return { buffers };
    }),
}));

class Client {
  private ws: WebSocket | null = null;
  private subs = new Map<string, { filter: EventFilter; limit: number }>();
  private backoff = 500;
  private timer: number | null = null;

  constructor() {
    this.connect();
  }

  private url() {
    const proto = location.protocol === "https:" ? "wss" : "ws";
    return `${proto}://${location.host}/ws/browser`;
  }

  private connect() {
    useLiveStore.getState().setStatus("connecting");
    const ws = new WebSocket(this.url());
    this.ws = ws;
    ws.onopen = () => {
      this.backoff = 500;
      useLiveStore.getState().setStatus("open");
      for (const [id, s] of this.subs) this.sendSubscribe(id, s.filter, s.limit);
    };
    ws.onmessage = (ev) => {
      let f: ServerToBrowserFrame;
      try {
        f = JSON.parse(ev.data);
      } catch {
        return;
      }
      const st = useLiveStore.getState();
      switch (f.type) {
        case "snapshot":
          st.setDevices(f.devices);
          st.snapshot(f.id, f.events);
          break;
        case "event":
          st.push(f.id, f.event);
          break;
        case "device.status":
          st.deviceStatus(f);
          break;
        case "run.status":
          st.setRunStatus(f);
          break;
        case "error":
          console.warn("ws error frame", f.message);
          break;
      }
    };
    ws.onclose = (ev) => {
      useLiveStore.getState().setStatus("closed");
      if (ev.code === 4401) {
        // Session gone: do not hammer the server with reconnects, go log in.
        redirectToLogin();
        return;
      }
      this.timer = window.setTimeout(() => this.connect(), this.backoff);
      this.backoff = Math.min(this.backoff * 2, 10_000);
    };
    ws.onerror = () => ws.close();
  }

  private sendSubscribe(id: string, filter: EventFilter, limit: number) {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify({ type: "subscribe", id, filter, snapshot: { limit } }));
  }

  subscribe(id: string, filter: EventFilter, limit = 200) {
    this.subs.set(id, { filter, limit });
    this.sendSubscribe(id, filter, limit);
  }

  unsubscribe(id: string) {
    this.subs.delete(id);
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify({ type: "unsubscribe", id }));
    useLiveStore.getState().drop(id);
  }
}

let client: Client | null = null;
export function wsClient(): Client {
  if (!client) client = new Client();
  return client;
}

export function useWsStatus(): WsStatus {
  useEffect(() => {
    wsClient();
  }, []);
  return useLiveStore((s) => s.status);
}

let subSeq = 0;

/** Subscribe to live events matching a filter; returns the ring buffer (snapshot + live). */
export function useLive(filter: EventFilter, opts: { limit?: number; enabled?: boolean } = {}): StoredEvent[] {
  const key = JSON.stringify(filter);
  const [id] = useState(() => `s${++subSeq}`);
  const enabled = opts.enabled ?? true;
  useEffect(() => {
    if (!enabled) return;
    wsClient().subscribe(id, JSON.parse(key), opts.limit ?? 200);
    return () => wsClient().unsubscribe(id);
  }, [id, key, enabled, opts.limit]);
  const buf = useLiveStore((s) => s.buffers[id]);
  return useMemo(() => buf ?? [], [buf]);
}

export function useDevicesLive(): Record<string, DevicePresence> {
  useEffect(() => {
    wsClient();
  }, []);
  return useLiveStore((s) => s.devices);
}

/** Merge history (REST) and live (WS) events, deduped on (deviceId, seq), ascending by (ts, seq). */
export function mergeEvents(history: StoredEvent[], live: StoredEvent[]): StoredEvent[] {
  const seen = new Map<string, StoredEvent>();
  for (const e of history) seen.set(`${e.deviceId}|${e.seq}`, e);
  for (const e of live) seen.set(`${e.deviceId}|${e.seq}`, e);
  return [...seen.values()].sort((a, b) => a.ts - b.ts || a.seq - b.seq || a.id - b.id);
}
